import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Newspaper,
  Image,
  FileText,
  Users,
  TrendingUp,
  Clock,
  BookOpen,
  GraduationCap,
  Building2,
  Mail,
  ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Dashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState({
    registrations: { total: 0, pending: 0, accepted: 0, rejected: 0, reviewed: 0 },
    news: 0,
    gallery: 0,
    research: 0,
    programs: 0,
    colleges: 0,
    contacts: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [regStats, newsData, galleryData, researchData, programsData, collegesData, contactsData] = await Promise.all([
        axios.get(`${API_URL}/api/registrations/stats/summary`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/news/all`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/gallery/all`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/research/all`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/programs/all`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/colleges/all`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/contacts/`, { headers }).catch(() => ({ data: [] }))
      ]);

      setStats({
        registrations: regStats.data || { total: 0, pending: 0, accepted: 0, rejected: 0, reviewed: 0 },
        news: newsData.data?.length || 0,
        gallery: galleryData.data?.length || 0,
        research: researchData.data?.length || 0,
        programs: programsData.data?.length || 0,
        colleges: collegesData.data?.length || 0,
        contacts: contactsData.data?.length || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, title, value, color, bgLight, subtext, link }) => {
    const cardContent = (
      <Card className="hover:shadow-lg transition-all border border-gray-100 h-full">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
              <p className={`text-3xl font-bold ${color}`}>{value}</p>
              {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
            </div>
            <div className={`w-14 h-14 rounded-2xl ${bgLight} flex items-center justify-center`}>
              <Icon className={`w-7 h-7 ${color}`} />
            </div>
          </div>
        </CardContent>
      </Card>
    );

    if (link) {
      return (
        <a href={link} className="block transition-transform hover:-translate-y-0.5">
          {cardContent}
        </a>
      );
    }
    return cardContent;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0F388E] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">جاري تحميل الإحصائيات...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0F388E] to-[#1E52B5] text-white p-8 rounded-2xl shadow-md relative overflow-hidden">
        <div className="relative z-10">
          <h1 className="text-3xl font-bold mb-2">لوحة التحكم الأكاديمية والإدارية</h1>
          <p className="text-blue-100 text-sm max-w-2xl">
            مرحباً بك في نظام الإدارة الشامل لجامعة ابن النفيس للعلوم الطبية. تحكم في جميع الأبحاث المنشورة، الأخبار، البرامج، الكليات، ورسائل واستفسارات الطلاب والزوار بسهولة تامة.
          </p>
        </div>
        <div className="absolute left-6 -bottom-10 opacity-10 pointer-events-none">
          <GraduationCap className="w-64 h-64 text-white" />
        </div>
      </div>

      {/* Grid 1: Key Counts */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard
          icon={BookOpen}
          title="الأبحاث العلمية المنشورة"
          value={stats.research}
          color="text-amber-600"
          bgLight="bg-amber-50"
          subtext="مفهرسة في Scopus وClarivate ومجلات محكمة"
          link="/admin/research"
        />
        <StatCard
          icon={GraduationCap}
          title="البرامج الأكاديمية"
          value={stats.programs}
          color="text-indigo-600"
          bgLight="bg-indigo-50"
          subtext="بكالوريوس وتخصصات طبية"
          link="/admin/programs"
        />
        <StatCard
          icon={Building2}
          title="الكليات الجامعية"
          value={stats.colleges}
          color="text-teal-600"
          bgLight="bg-teal-50"
          subtext="طب الأسنان، والصيدلة والعلوم الطبية"
          link="/admin/colleges"
        />
        <StatCard
          icon={Mail}
          title="رسائل واستفسارات الزوار"
          value={stats.contacts}
          color="text-rose-600"
          bgLight="bg-rose-50"
          subtext="استفسارات واردة عبر الموقع"
          link="/admin/contacts"
        />
      </div>

      {/* Grid 2: Operational Counts */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard
          icon={FileText}
          title="إجمالي طلبات التسجيل"
          value={stats.registrations.total}
          color="text-blue-600"
          bgLight="bg-blue-50"
          link="/admin/registrations"
        />
        <StatCard
          icon={Clock}
          title="طلبات قيد المراجعة"
          value={stats.registrations.pending}
          color="text-amber-500"
          bgLight="bg-amber-50"
          link="/admin/registrations"
        />
        <StatCard
          icon={Newspaper}
          title="الأخبار والفعاليات"
          value={stats.news}
          color="text-emerald-600"
          bgLight="bg-emerald-50"
          link="/admin/news"
        />
        <StatCard
          icon={Image}
          title="صور المعرض"
          value={stats.gallery}
          color="text-purple-600"
          bgLight="bg-purple-50"
          link="/admin/gallery"
        />
      </div>

      {/* Details & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Registrations breakdown */}
        <Card className="shadow-sm">
          <CardHeader className="pb-3 border-b border-gray-100">
            <CardTitle className="text-[#0F388E] text-lg font-bold flex items-center justify-between">
              <span>حالة طلبات التسجيل الأكاديمي</span>
              <a href="/admin/registrations" className="text-xs text-blue-600 hover:underline flex items-center gap-1 font-normal">
                عرض الكل
                <ArrowLeft className="w-3.5 h-3.5" />
              </a>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-5">
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3.5 bg-amber-50/70 border border-amber-100 rounded-xl">
                <span className="text-gray-700 font-medium">قيد المراجعة</span>
                <span className="text-2xl font-bold text-amber-600">{stats.registrations.pending}</span>
              </div>
              <div className="flex items-center justify-between p-3.5 bg-blue-50/70 border border-blue-100 rounded-xl">
                <span className="text-gray-700 font-medium">تمت المراجعة</span>
                <span className="text-2xl font-bold text-blue-600">{stats.registrations.reviewed}</span>
              </div>
              <div className="flex items-center justify-between p-3.5 bg-emerald-50/70 border border-emerald-100 rounded-xl">
                <span className="text-gray-700 font-medium">مقبولة</span>
                <span className="text-2xl font-bold text-emerald-600">{stats.registrations.accepted}</span>
              </div>
              <div className="flex items-center justify-between p-3.5 bg-rose-50/70 border border-rose-100 rounded-xl">
                <span className="text-gray-700 font-medium">مرفوضة</span>
                <span className="text-2xl font-bold text-rose-600">{stats.registrations.rejected}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="shadow-sm">
          <CardHeader className="pb-3 border-b border-gray-100">
            <CardTitle className="text-[#0F388E] text-lg font-bold">إجراءات سريعة لإدارة المحتوى</CardTitle>
          </CardHeader>
          <CardContent className="pt-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a
                href="/admin/research"
                className="p-4 bg-gradient-to-br from-amber-600 to-amber-700 text-white rounded-xl hover:shadow-md transition-all flex flex-col justify-between"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-white" />
                  </div>
                  <p className="font-bold text-sm">إدارة الأبحاث</p>
                </div>
                <p className="text-xs text-amber-100">إضافة بحث، رفع ملفات PDF وتحديث بيانات النشر</p>
              </a>

              <a
                href="/admin/news"
                className="p-4 bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-xl hover:shadow-md transition-all flex flex-col justify-between"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center">
                    <Newspaper className="w-5 h-5 text-white" />
                  </div>
                  <p className="font-bold text-sm">نشر خبر جديد</p>
                </div>
                <p className="text-xs text-blue-100">نشر فعاليات وأخبار الجامعة والمؤتمرات</p>
              </a>

              <a
                href="/admin/programs"
                className="p-4 bg-gradient-to-br from-indigo-600 to-indigo-700 text-white rounded-xl hover:shadow-md transition-all flex flex-col justify-between"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-white" />
                  </div>
                  <p className="font-bold text-sm">البرامج الأكاديمية</p>
                </div>
                <p className="text-xs text-indigo-100">تعديل التخصصات، الخطط والمخرجات</p>
              </a>

              <a
                href="/admin/contacts"
                className="p-4 bg-gradient-to-br from-rose-600 to-rose-700 text-white rounded-xl hover:shadow-md transition-all flex flex-col justify-between"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <p className="font-bold text-sm">رسائل التواصل</p>
                </div>
                <p className="text-xs text-rose-100">مراجعة رسائل واستفسارات الزوار والرد عليها</p>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;